import axios, { type AxiosError } from 'axios';
import { API_BASE_URL } from '../config/api';

let csrfProof: string | null = null;
const CSRF_COOKIE_NAME = 'ta_admin_csrf';

function csrfCookie(): string | null {
  try {
    const prefix = `${encodeURIComponent(CSRF_COOKIE_NAME)}=`;
    const entry = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(prefix));
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
  } catch {
    return null;
  }
}

export function getAdminCsrfProof(): string | null {
  return csrfCookie() ?? csrfProof;
}

export function setAdminCsrfProof(value: string | null): void {
  csrfProof = value;
}

/**
 * The single administrator Gateway client. Authentication is the opaque,
 * HttpOnly admin session cookie; JavaScript never stores or sends bearer
 * credentials. Browsers supply the Origin header themselves.
 */
export function createAdminGatewayApi() {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'X-TripAlta-Client': 'admin',
      'X-Client-Platform': 'ADMIN_WEB',
    },
  });
  client.interceptors.request.use(config => {
    const method = (config.method ?? 'GET').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const proof = getAdminCsrfProof();
      if (proof) config.headers['X-CSRF-Token'] = proof;
    }
    return config;
  });
  client.interceptors.response.use(
    response => {
      const proof = response.headers['x-csrf-token'];
      if (typeof proof === 'string' && proof) setAdminCsrfProof(proof);
      return response;
    },
    (error: AxiosError) => {
      const proof = error.response?.headers?.['x-csrf-token'];
      if (typeof proof === 'string' && proof) setAdminCsrfProof(proof);
      return Promise.reject(error);
    }
  );
  return client;
}

export const adminGatewayApi = createAdminGatewayApi();

export const api = adminGatewayApi;
